import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { employeesApi } from "@/api";
import { useLang, fmtMoney } from "@/i18n";

const EMPTY = { first_name: "", last_name: "", afm: "", amka: "", ama: "", position: "", father_name: "", mother_name: "", specialty_code: "", coverage_code: "0101",
  hire_date: new Date().toISOString().slice(0, 10),
  birth_date: "", children: 0, employment_type: "full_time", weekly_hours: 40, gross_salary: "", iban: "", phone: "", email: "", active: true };

const Field = ({ label, children, error, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
    {error && <p className="text-xs text-rose-600" data-testid="field-error">{error}</p>}
  </div>
);

export const EmployeeDialog = ({ open, onOpenChange, employee, onSaved }) => {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setForm(employee ? { ...EMPTY, ...employee, birth_date: employee.birth_date || "" } : EMPTY); setErrors({}); }
  }, [open, employee]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const hourly = form.gross_salary && form.weekly_hours ? Number(form.gross_salary) / (Number(form.weekly_hours) * 4.33) : 0;

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = t("required");
    if (!form.last_name.trim()) errs.last_name = t("required");
    if (!form.hire_date) errs.hire_date = t("required");
    if (!Number(form.gross_salary) || Number(form.gross_salary) <= 0) errs.gross_salary = t("required");
    if (form.afm && !/^\d{9}$/.test(form.afm.trim())) errs.afm = t("afm_invalid");
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const payload = { ...form, children: Number(form.children) || 0, weekly_hours: Number(form.weekly_hours) || 40,
        gross_salary: Number(form.gross_salary), birth_date: form.birth_date || null };
      delete payload.id; delete payload.created_at;
      await (employee ? employeesApi.update(employee.id, payload) : employeesApi.create(payload));
      toast.success(t("employee_saved"));
      onSaved();
      onOpenChange(false);
    } catch { toast.error(t("error")); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{employee ? t("edit_employee") : t("new_employee")}</DialogTitle>
          <DialogDescription className="sr-only">{t("employee")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label={t("last_name")} error={errors.last_name}><Input value={form.last_name} onChange={set("last_name")} data-testid="employee-form-last-name-input" autoFocus /></Field>
          <Field label={t("first_name")} error={errors.first_name}><Input value={form.first_name} onChange={set("first_name")} data-testid="employee-form-first-name-input" /></Field>
          <Field label={t("position")}><Input value={form.position} onChange={set("position")} data-testid="employee-form-position-input" /></Field>
          <Field label={t("father_name")}><Input value={form.father_name} onChange={set("father_name")} data-testid="employee-form-father-input" /></Field>
          <Field label={t("mother_name")}><Input value={form.mother_name} onChange={set("mother_name")} data-testid="employee-form-mother-input" /></Field>
          <Field label={t("specialty_code")}><Input value={form.specialty_code} onChange={set("specialty_code")} className="font-mono" placeholder="000000" data-testid="employee-form-specialty-input" /></Field>
          <Field label={t("coverage_code")}><Input value={form.coverage_code} onChange={set("coverage_code")} className="font-mono" placeholder="0101" data-testid="employee-form-coverage-input" /></Field>
          <Field label={t("vat_id")} error={errors.afm}><Input value={form.afm} onChange={set("afm")} className="font-mono" data-testid="employee-form-afm-input" /></Field>
          <Field label={t("amka")}><Input value={form.amka} onChange={set("amka")} className="font-mono" data-testid="employee-form-amka-input" /></Field>
          <Field label={t("ama")}><Input value={form.ama} onChange={set("ama")} className="font-mono" data-testid="employee-form-ama-input" /></Field>
          <Field label={t("hire_date")} error={errors.hire_date}><DateInput value={form.hire_date} onChange={set("hire_date")} className="font-mono" data-testid="employee-form-hire-date-input" /></Field>
          <Field label={t("birth_date")}><DateInput value={form.birth_date} onChange={set("birth_date")} className="font-mono" data-testid="employee-form-birth-date-input" /></Field>
          <Field label={t("children")}><Input type="number" min="0" value={form.children} onChange={set("children")} className="font-mono" data-testid="employee-form-children-input" /></Field>
          <Field label={t("employment_type")}>
            <Select value={form.employment_type} onValueChange={(v) => setForm((f) => ({ ...f, employment_type: v, weekly_hours: v === "full_time" ? 40 : f.weekly_hours === 40 ? 20 : f.weekly_hours }))}>
              <SelectTrigger data-testid="employee-form-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full_time">{t("full_time")}</SelectItem>
                <SelectItem value="part_time">{t("part_time")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("weekly_hours")}><Input type="number" step="0.5" min="1" max="40" value={form.weekly_hours} onChange={set("weekly_hours")} className="font-mono" data-testid="employee-form-hours-input" /></Field>
          <Field label={t("gross_salary")} error={errors.gross_salary}>
            <Input type="number" step="0.01" min="0" value={form.gross_salary} onChange={set("gross_salary")} className="font-mono" data-testid="employee-form-salary-input" />
            {hourly > 0 && <p className="text-xs text-slate-500 font-mono">{t("hourly_rate")}: {fmtMoney(hourly)}</p>}
          </Field>
          <Field label={t("iban")} className="sm:col-span-2"><Input value={form.iban} onChange={set("iban")} className="font-mono" data-testid="employee-form-iban-input" /></Field>
          <Field label={t("phone")}><Input value={form.phone} onChange={set("phone")} className="font-mono" data-testid="employee-form-phone-input" /></Field>
          <Field label={t("email")} className="sm:col-span-2"><Input type="email" value={form.email} onChange={set("email")} data-testid="employee-form-email-input" /></Field>
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={form.active} onCheckedChange={set("active")} data-testid="employee-form-active-switch" />
            <Label className="text-sm">{form.active ? t("active") : t("inactive")}</Label>
          </div>
          <DialogFooter className="sm:col-span-3 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="employee-form-cancel-button">{t("cancel")}</Button>
            <Button type="submit" disabled={saving} className="bg-blue-800 hover:bg-blue-700" data-testid="employee-form-submit-button">{t("save")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
